/**
 * Static second row of the SRP header: horizontally scrollable facet pills.
 * Renders from initial facets data (from Algolia) so the full desktop filter bar
 * is present in the static shell without lazy loading.
 */
import React, { useMemo } from 'react';
import { Icon, type IconName } from '../../../components/icons/Icon';
import { Link } from '../../../components/Link';
import { CompanyName } from '../../../components/CompanyName';
import { Logo } from '../../../components/navigation';
import { generateUrlFromIndexUiState } from '../../../utils/algolia/url-handling';
import { shouldIncludeFacetInTopFilters, TOP_FILTER_ATTRIBUTE_ORDER, ALL_TOP_FILTER_ATTRIBUTES_ORDER } from '../../../layouts/helpers/topFilterConfig';
import type { SrpFacetsData } from '../../../features/srp/SrpViewModel';

const MAX_FACETS = 20;

/** Icon name per attribute to match TopFilters (file-based icons). */
const ATTRIBUTE_ICON_NAME: Record<string, IconName> = {
  remoteness: 'mapPin',
  location: 'mapPin',
  jobType: 'briefcase',
  normalisedRoleLevel: 'chartBar',
  companyIndustry: 'buildingOffice2',
  companyId: 'buildingOffice2',
  requiredLanguages: 'globeAlt',
  benefits: 'briefcase',
  skillsAndTechnologies: 'briefcase',
  field: 'chartBar',
  educationLevel: 'chartBar',
  peopleManagementTier: 'chartBar',
};

const ATTRIBUTE_COLOR_CLASS: Record<string, string> = {
  remoteness: 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700',
  location: 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-300 dark:border-green-700',
  jobType: 'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border-purple-300 dark:border-purple-700',
  normalisedRoleLevel: 'bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border-orange-300 dark:border-orange-700',
  requiredLanguages: 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border-indigo-300 dark:border-indigo-700',
  companyId: 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border-indigo-300 dark:border-indigo-700',
  companyIndustry: 'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border-purple-300 dark:border-purple-700',
  field: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600',
  peopleManagementTier: 'bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border-orange-300 dark:border-orange-700',
};

/** Full class strings for pill states so Tailwind can scan literals (no dynamic concatenation). */
const PILL_ACTIVE_CLASS_EXCLUDED =
  'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-300 dark:border-red-700 shadow-sm border-2';
const PILL_ACTIVE_CLASS_INACTIVE =
  'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-transparent hover:bg-gray-200 dark:hover:bg-gray-600 border-2';
const PILL_ACTIVE_CLASS_ACTIVE: Record<string, string> = {
  remoteness: 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700 shadow-sm border-2',
  location: 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-300 dark:border-green-700 shadow-sm border-2',
  jobType: 'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border-purple-300 dark:border-purple-700 shadow-sm border-2',
  normalisedRoleLevel: 'bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border-orange-300 dark:border-orange-700 shadow-sm border-2',
  requiredLanguages: 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border-indigo-300 dark:border-indigo-700 shadow-sm border-2',
  companyId: 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border-indigo-300 dark:border-indigo-700 shadow-sm border-2',
  companyIndustry: 'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border-purple-300 dark:border-purple-700 shadow-sm border-2',
  field: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 shadow-sm border-2',
  peopleManagementTier: 'bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border-orange-300 dark:border-orange-700 shadow-sm border-2',
};

export interface StaticTopFiltersProps {
  facets: SrpFacetsData;
  refinementList: Record<string, string[]>;
  pathname: string;
  indexName: string;
  query: string;
  /** When set, show logo on left of scroll row on mobile (lg:hidden), matching TopFilters. */
  locale?: string;
  logoTooltip?: string;
}

function toggleRefinement(
  refinementList: Record<string, string[]>,
  attribute: string,
  value: string
): Record<string, string[]> {
  const current = refinementList[attribute] ?? [];
  const has = current.some((v) => v.toLowerCase() === value.toLowerCase());
  const next = has
    ? current.filter((v) => v.toLowerCase() !== value.toLowerCase())
    : [...current, value];
  const out = { ...refinementList };
  if (next.length === 0) {
    delete out[attribute];
  } else {
    out[attribute] = next;
  }
  return out;
}

export function StaticTopFilters({
  facets,
  refinementList,
  pathname,
  indexName,
  query,
  locale,
  logoTooltip,
}: StaticTopFiltersProps): React.ReactElement | null {
  const pills = useMemo(() => {
    const isInRefinement = (attr: string, value: string): boolean =>
      (refinementList[attr] ?? []).some((v) => v.toLowerCase() === value.toLowerCase());
    const isInExclude = (attr: string, value: string): boolean =>
      (refinementList[`exclude_${attr}`] ?? []).some((v) => v.toLowerCase() === value.toLowerCase());

    const raw: Array<{ attribute: string; value: string; label: string; count: number; isActive: boolean; isExcluded: boolean }> = [];
    for (const attr of ALL_TOP_FILTER_ATTRIBUTES_ORDER) {
      const items = facets[attr] ?? [];
      for (const item of items) {
        raw.push({
          attribute: attr,
          value: item.value,
          label: item.label,
          count: item.count,
          isActive: item.selected ?? isInRefinement(attr, item.value),
          isExcluded: isInExclude(attr, item.value),
        });
      }
    }
    const allowedFacets = raw.filter((f) => shouldIncludeFacetInTopFilters(f.attribute));
    const activeFacets = allowedFacets.filter((f) => f.isActive || f.isExcluded);
    const inactiveFacets = allowedFacets.filter((f) => !f.isActive && !f.isExcluded);

    const attributeOrder = TOP_FILTER_ATTRIBUTE_ORDER as readonly string[];
    const activeByAttr = new Map<string, typeof activeFacets>();
    for (const f of activeFacets) {
      if (!activeByAttr.has(f.attribute)) activeByAttr.set(f.attribute, []);
      activeByAttr.get(f.attribute)!.push(f);
    }
    const result: typeof raw = [];
    for (const attr of attributeOrder) {
      const list = activeByAttr.get(attr);
      if (list) result.push(...list);
    }
    const addedAttributes = new Set(attributeOrder.filter((a) => activeByAttr.has(a)));
    for (const f of activeFacets) {
      if (!addedAttributes.has(f.attribute)) {
        result.push(f);
        addedAttributes.add(f.attribute);
      }
    }
    const sortedInactive = [...inactiveFacets].sort((a, b) => b.count - a.count);
    const remainingSlots = Math.max(0, MAX_FACETS - result.length);
    result.push(...sortedInactive.slice(0, remainingSlots));
    return result;
  }, [facets, refinementList]);

  if (pills.length === 0) return null;

  return (
    <aside className="relative" style={{ height: '64px' }} aria-label="Filter categories">
      <div className="absolute left-0 top-0 bottom-4 w-8 bg-gradient-to-r from-white dark:from-gray-800 to-transparent pointer-events-none z-10 lg:left-0" />
      <div className="absolute right-0 top-0 bottom-4 w-8 bg-gradient-to-l from-white dark:from-gray-800 to-transparent pointer-events-none z-10" />
      <div className="h-full flex items-center min-w-0">
        {/* Logo on left of scroll row – mobile only (lg:hidden), matches TopFilters */}
        {locale != null && logoTooltip != null && (
          <div className="flex-shrink-0 pl-3 pr-2 lg:hidden flex items-center">
            <Logo tooltipLabel={logoTooltip} locale={locale} variant="icon" />
          </div>
        )}
        <div
          className="h-full flex-1 min-w-0 flex items-center overflow-x-auto pl-2 pr-4 lg:pl-2 lg:pr-4 xl:px-20 gap-1"
          style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(156, 163, 175, 0.5) transparent' }}
        >
          {pills.map((pill) => {
            const href = generateUrlFromIndexUiState(
              {
                query,
                refinementList: toggleRefinement(refinementList, pill.attribute, pill.value),
                page: 0,
              },
              indexName,
              pathname
            );
            const activeClass = pill.isExcluded
              ? PILL_ACTIVE_CLASS_EXCLUDED
              : pill.isActive
              ? (PILL_ACTIVE_CLASS_ACTIVE[pill.attribute] ?? PILL_ACTIVE_CLASS_INACTIVE)
              : PILL_ACTIVE_CLASS_INACTIVE;

            return (
              <Link
                key={`${pill.attribute}-${pill.value}`}
                href={href ?? '#'}
                className={`flex items-center gap-1.5 rounded-full px-4 py-2 font-medium text-sm whitespace-nowrap border-2 flex-shrink-0 ${activeClass}`}
                aria-label={`${pill.label} (${pill.count} jobs)`}
              >
                {ATTRIBUTE_ICON_NAME[pill.attribute] ? (
                <Icon
                  name={ATTRIBUTE_ICON_NAME[pill.attribute]}
                  useMaskForColor
                  className="w-3 h-3 flex-shrink-0"
                />
              ) : null}
                {pill.attribute === 'companyId' ? (
                  <CompanyName name={pill.label} />
                ) : (
                  <span>{pill.label}</span>
                )}
                <span className="text-xs opacity-75">({pill.count})</span>
              </Link>
            );
          })}
        </div>
      </div>
    </aside>
  );
}
